'use client';

import * as Sentry from '@sentry/nextjs';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createContext, PropsWithChildren, use, useCallback, useContext, useEffect, useState } from 'react';
import { SEARCH_PARAM_KEYS } from '@/constants/auth';
import { useAppLoaderContext } from '@/context/AppLoaderContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useUAContext } from '@/context/UAContext';
import useAuthBootstrap from '@/hooks/auth/useAuthBootstrap';
import useVerifyTokenPolling from '@/hooks/auth/useVerifyTokenPolling';
import useHash from '@/hooks/useHash';
import { getLoginUrl, getLogoutUrl, LoginParams } from '@/lib/url';
import { logFeedLikeLoginDebug } from '@/lib/utils';
import type { AuthSnapshot } from '@/server/lib/auth-snapshot';
const ConfirmDialog = dynamic(() => import('@/components/ConfirmDialog'), {
  ssr: false
});
const SuspensionModal = dynamic(() => import('@/components/SuspensionModal'));
type AuthContextType = {
  currentUserId: string;
  isSignedIn: boolean;
  isOver18: boolean | null;
  isSuspended: boolean;
  isGamePlus: boolean;
  handleLogin: (params?: LoginParams) => void;
  handleLogout: () => void;
  htcAuthKey: string;
};
const AuthContext = createContext<AuthContextType | null>(null);
type AuthProviderProps = PropsWithChildren<{
  snapshotPromise: Promise<AuthSnapshot>;
  verifyTokenPollIntervalMinutes: number;
}>;
export const AuthProvider = ({
  children,
  snapshotPromise,
  verifyTokenPollIntervalMinutes
}: AuthProviderProps) => {
  const snapshot = use(snapshotPromise);
  const [isSignedIn, setIsSignedIn] = useState(snapshot.isSignedIn);
  const [isVerifyTokenLogoutDialogOpen, setIsVerifyTokenLogoutDialogOpen] = useState(false);
  const {
    isSuspended = false,
    suspendedAt = ''
  } = snapshot;
  const isOver18 = snapshot.profile?.isOver18 ?? null;
  const isGamePlus = isSignedIn && (snapshot.profile?.isGamePlus ?? false);
  const hash = useHash();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const appState = searchParams.get(SEARCH_PARAM_KEYS.APP_STATE);
  const {
    setIsAppLoading
  } = useAppLoaderContext();
  const {
    removeStore,
    appConfig
  } = useConfigsContext();
  const {
    isMobileApp
  } = useUAContext();
  const t = useTranslations();
  useEffect(() => {
    setIsSignedIn(snapshot.isSignedIn);
  }, [snapshot.isSignedIn]);
  const handleLogout = useCallback(async () => {
    setIsAppLoading(true);
    const logoutUrl = await getLogoutUrl();
    router.push(logoutUrl);
  }, [router, setIsAppLoading]);
  const vodafoneURLPostfix = appConfig?.features?.vodafone_url_postfix;
  const handleLogin = useCallback(async ({
    callback = '',
    loginParams
  }: LoginParams = {}) => {
    const shouldSkipLoginLoader = isMobileApp && pathname.includes('/feed');
    if (isMobileApp && pathname.includes('/feed')) {
      logFeedLikeLoginDebug({
        stage: 'auth',
        event: 'handle-login-start',
        pathname,
        callback,
        hasAppState: Boolean(appState),
        loginParamKeys: loginParams ? Object.keys(loginParams) : [],
        shouldSkipLoginLoader
      });
    }
    if (!shouldSkipLoginLoader) {
      setIsAppLoading(true);
    }
    let loginStage = 'get-login-url';
    try {
      // Get app_state from current URL and add it to loginParams
      const enhancedLoginParams = {
        ...loginParams,
        ...(appState && {
          app_state: appState
        }),
        // Mobile app: add authorities parameter for authentication
        ...(isMobileApp && {
          authorities: 'htc.com'
        })
      };
      const loginUrl = await getLoginUrl({
        callback,
        loginParams: enhancedLoginParams,
        vodafoneURLPostfix
      });
      loginStage = 'navigation';
      if (isMobileApp) {
        window.location.assign(loginUrl);
        return;
      }
      router.push(loginUrl);
    } catch (error) {
      if (!shouldSkipLoginLoader) {
        setIsAppLoading(false);
      }
      Sentry.captureException(new Error('Failed to start login', {
        cause: error
      }), {
        tags: {
          feature: 'auth',
          source: 'login',
          stage: loginStage
        }
      });
    }
  }, [router, setIsAppLoading, vodafoneURLPostfix, appState, isMobileApp, pathname]);
  useEffect(() => {
    if (hash === '#logout') {
      setIsAppLoading(true);
      setIsSignedIn(false);
      removeStore();
      const newURL = pathname + searchParams.toString();
      window.history.replaceState({}, document.title, newURL);
      setIsAppLoading(false);
    }
  }, [hash, pathname, removeStore, searchParams, setIsAppLoading]);
  const handleSignedOut = useCallback(() => {
    setIsSignedIn(false);
    removeStore();
  }, [removeStore]);
  const handleInvalidSession = useCallback(() => {
    handleSignedOut();
    handleLogout();
  }, [handleLogout, handleSignedOut]);
  useVerifyTokenPolling({
    authKey: snapshot.htcAuthKey,
    isExternalIframe: snapshot.isExternalIframe ?? false,
    isSignedIn,
    onLogoutRequired: () => setIsVerifyTokenLogoutDialogOpen(true),
    pollIntervalMinutes: verifyTokenPollIntervalMinutes
  });

  // Hydrate each distinct server auth snapshot. Login-status still runs at most once per mount,
  // after hydration, so its rotated-token write cannot race the store update.
  useAuthBootstrap(snapshot, {
    onSignedOut: handleSignedOut,
    onInvalidSession: handleInvalidSession
  });
  return <AuthContext.Provider value={{
    currentUserId: snapshot.profile?.userId ?? '',
    isSignedIn,
    isOver18,
    isSuspended,
    isGamePlus,
    handleLogin,
    handleLogout,
    htcAuthKey: snapshot.htcAuthKey
  }}>
      {children}
      {isSuspended && <SuspensionModal open suspendedAt={suspendedAt} />}
      {isVerifyTokenLogoutDialogOpen && <ConfirmDialog content={t('verify-token.logout-confirmation.content')} dismissible={false} onConfirm={handleLogout} onOpenChange={setIsVerifyTokenLogoutDialogOpen} open title={t('verify-token.logout-confirmation.title')} withSeparator={false} />}
    </AuthContext.Provider>;
};
export const useAuthContext = () => {
  const value = useContext(AuthContext);
  if (value == null) {
    throw new Error("useAuthContext cannot be used outside of AuthProvider");
  }
  return value;
};